import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Settings, CheckCircle, XCircle, AlertCircle, Eye, EyeOff, ArrowLeft, Edit, Trash2, FileCode, Save, RefreshCw, ShieldAlert } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';

const IntegrationSettings = () => {
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [viewingConfig, setViewingConfig] = useState(null);
  const [viewingCredentials, setViewingCredentials] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [showCredentials, setShowCredentials] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEnvEditor, setShowEnvEditor] = useState(false);
  const [envVars, setEnvVars] = useState({});
  const [envEditing, setEnvEditing] = useState({});
  const [envSaving, setEnvSaving] = useState(false);
  const [envShowValues, setEnvShowValues] = useState(false);
  const [envConfirmOpen, setEnvConfirmOpen] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();

  // API call helper function
  const apiCall = async (endpoint, options = {}) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token');
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${BACKEND_URL}/api${endpoint}`, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      // Clone response before reading to avoid "body stream already read" error
      let errorMessage = 'API request failed';
      try {
        const errorData = await response.clone().json();
        errorMessage = errorData.detail || errorMessage;
      } catch {
        // If JSON parsing fails, try text
        try {
          errorMessage = await response.text() || errorMessage;
        } catch {
          // Use status text as fallback
          errorMessage = response.statusText || `HTTP ${response.status}`;
        }
      }
      throw new Error(errorMessage);
    }
    
    return response.json();
  };

  // Integration definitions with required credential fields
  const integrationDefinitions = {
    zoho_books: {
      service: 'zoho_books',
      category: 'financial',
      integration_name: 'Zoho Books / GST Billing',
      description: 'Required for GST billing, vendor payouts, and platform revenue tracking',
      fields: [
        { key: 'client_id', label: 'Zoho Client ID', type: 'text', required: true },
        { key: 'client_secret', label: 'Zoho Client Secret', type: 'password', required: true },
        { key: 'refresh_token', label: 'Zoho Refresh Token', type: 'password', required: true },
        { key: 'organization_id', label: 'Organization ID', type: 'text', required: true },
        { key: 'api_base_url', label: 'API Base URL', type: 'text', required: false, default: 'https://books.zoho.com/api/v3' }
      ]
    },
    zoho_invoice: {
      service: 'zoho_invoice',
      category: 'financial',
      integration_name: 'Zoho Invoice',
      description: 'Automated invoice generation for vendors and customers',
      fields: [
        { key: 'client_id', label: 'Zoho Client ID', type: 'text', required: true },
        { key: 'client_secret', label: 'Zoho Client Secret', type: 'password', required: true },
        { key: 'redirect_uri', label: 'OAuth Redirect URI', type: 'text', required: true, default: 'https://seller.aniyor.net/api/auth/zoho/callback' },
        { key: 'organization_id', label: 'Organization ID', type: 'text', required: false, placeholder: 'Set after OAuth connection' }
      ]
    },
    zoho_crm: {
      service: 'zoho_crm',
      category: 'crm',
      integration_name: 'Zoho CRM',
      description: 'Customer relationship management - sync leads, contacts, and deals',
      fields: [
        { key: 'client_id', label: 'Zoho CRM Client ID', type: 'text', required: true },
        { key: 'client_secret', label: 'Zoho CRM Client Secret', type: 'password', required: true },
        { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: true },
        { key: 'api_domain', label: 'API Domain', type: 'select', options: ['zoho.com', 'zoho.in', 'zoho.eu', 'zoho.com.au'], required: true, default: 'zoho.in' },
        { key: 'sync_leads', label: 'Sync Leads', type: 'checkbox', required: false, default: true },
        { key: 'sync_contacts', label: 'Sync Contacts (Customers)', type: 'checkbox', required: false, default: true },
        { key: 'sync_deals', label: 'Sync Deals (Orders)', type: 'checkbox', required: false, default: true },
        { key: 'sync_vendors', label: 'Sync Vendors', type: 'checkbox', required: false, default: false }
      ]
    },
    razorpayx: {
      service: 'razorpayx',
      category: 'financial',
      integration_name: 'RazorpayX Payouts',
      description: 'Automated vendor payouts via bank transfer, UPI, or wallet',
      fields: [
        { key: 'key_id', label: 'RazorpayX Key ID', type: 'text', required: true },
        { key: 'key_secret', label: 'RazorpayX Key Secret', type: 'password', required: true },
        { key: 'account_number', label: 'Account Number', type: 'text', required: true },
        { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', required: false }
      ]
    },
    google_places: {
      service: 'google_places',
      category: 'reviews',
      integration_name: 'Google Places API',
      description: 'Required for pulling external reviews from Google Business profiles',
      fields: [
        { key: 'api_key', label: 'Google API Key', type: 'password', required: true },
        { key: 'service_account_key', label: 'Service Account Key (JSON)', type: 'textarea', required: false }
      ]
    },
    shiprocket: {
      service: 'shiprocket',
      category: 'logistics',
      integration_name: 'Shiprocket',
      description: 'Platform-integrated shipping rates for vendors',
      fields: [
        { key: 'email', label: 'Shiprocket Email', type: 'email', required: true },
        { key: 'password', label: 'Shiprocket Password', type: 'password', required: true },
        { key: 'api_key', label: 'API Key', type: 'password', required: true },
        { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', required: false }
      ]
    },
    delhivery: {
      service: 'delhivery',
      category: 'logistics',
      integration_name: 'Delhivery',
      description: 'Alternative shipping provider with competitive rates',
      fields: [
        { key: 'api_key', label: 'API Key', type: 'password', required: true },
        { key: 'client_id', label: 'Client ID', type: 'text', required: true },
        { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', required: false }
      ]
    },
    whatsapp: {
      service: 'whatsapp',
      category: 'crm',
      integration_name: 'WhatsApp Business API',
      description: 'Automated customer messaging for bookings, leads, and reminders',
      fields: [
        { key: 'provider', label: 'Provider', type: 'select', options: ['twilio', 'meta', 'vonage'], required: true },
        { key: 'app_id', label: 'App ID / Account SID', type: 'text', required: true },
        { key: 'access_token', label: 'Access Token / Auth Token', type: 'password', required: true },
        { key: 'phone_number_id', label: 'Phone Number ID', type: 'text', required: false }
      ]
    },
    emovur: {
      service: 'emovur',
      category: 'crm',
      integration_name: 'Emovur WhatsApp BSP',
      description: 'WhatsApp Business Solution Provider for automated messaging and shared inbox',
      fields: [
        { key: 'api_key', label: 'Emovur API Key', type: 'password', required: true },
        { key: 'api_secret', label: 'Emovur API Secret', type: 'password', required: true },
        { key: 'base_url', label: 'API Base URL', type: 'text', required: false, default: 'https://api.emovur.com/v1' },
        { key: 'whatsapp_number', label: 'WhatsApp Business Number', type: 'text', required: true, placeholder: '+919876543210' },
        { key: 'webhook_verify_token', label: 'Webhook Verify Token', type: 'text', required: false }
      ]
    },
    google_oauth: {
      service: 'google_oauth',
      category: 'auth',
      integration_name: 'Google OAuth',
      description: 'Enable Google Sign-In for users and vendors',
      fields: [
        { key: 'client_id', label: 'Google Client ID', type: 'text', required: true },
        { key: 'client_secret', label: 'Google Client Secret', type: 'password', required: true },
        { key: 'redirect_uri', label: 'OAuth Redirect URI', type: 'text', required: false, default: 'https://seller.aniyor.net/api/auth/google/callback' }
      ]
    },
    msg91: {
      service: 'msg91',
      category: 'communications',
      integration_name: 'MSG91 SMS Gateway',
      description: 'SMS gateway for OTP verification and transactional messages',
      fields: [
        { key: 'auth_key', label: 'MSG91 Auth Key', type: 'password', required: true, placeholder: 'Your MSG91 authkey from dashboard' },
        { key: 'sender_id', label: 'Sender ID (6 chars)', type: 'text', required: true, placeholder: 'e.g., ANIYORMRKT' },
        { key: 'template_id', label: 'OTP Template ID', type: 'text', required: true, placeholder: 'Template ID from MSG91 Dashboard' },
        { key: 'template_content', label: 'Template Content (DLT Approved)', type: 'textarea', required: true, placeholder: 'Paste your DLT template here. E.g.: Your OTP is ##number##. Valid for 5 mins.' },
        { key: 'country_code', label: 'Default Country Code', type: 'text', required: false, default: '91', placeholder: '91 for India' },
        { key: 'otp_length', label: 'OTP Length', type: 'number', required: false, default: '6', placeholder: '4-6 digits' },
        { key: 'otp_expiry', label: 'OTP Expiry (minutes)', type: 'number', required: false, default: '5', placeholder: '5-30 minutes' }
      ]
    },
    shopify_master: {
      service: 'shopify_master',
      category: 'sales_channels',
      integration_name: 'Master Shopify Store',
      description: 'Global theme management and platform-wide scripts',
      fields: [
        { key: 'store_url', label: 'Store URL', type: 'text', required: true, placeholder: 'your-store.myshopify.com' },
        { key: 'api_key', label: 'API Key', type: 'text', required: true },
        { key: 'api_password', label: 'API Password', type: 'password', required: true },
        { key: 'shared_secret', label: 'Shared Secret', type: 'password', required: true },
        { key: 'api_version', label: 'API Version', type: 'text', required: false, default: '2024-01' }
      ]
    },
    sendgrid: {
      service: 'sendgrid',
      category: 'communications',
      integration_name: 'SendGrid Email',
      description: 'Transactional emails for orders, notifications, and marketing',
      fields: [
        { key: 'api_key', label: 'SendGrid API Key', type: 'password', required: true },
        { key: 'from_email', label: 'From Email', type: 'email', required: true },
        { key: 'from_name', label: 'From Name', type: 'text', required: true }
      ]
    },
    zeptomail: {
      service: 'zeptomail',
      category: 'communications',
      integration_name: 'ZeptoMail (Zoho)',
      description: 'Transactional emails via Zoho ZeptoMail - OTP, order confirmations, notifications, and vendor communications',
      fields: [
        { key: 'enabled', label: 'Enable ZeptoMail', type: 'checkbox', required: false, default: true },
        { key: 'section_api', label: 'API Configuration', type: 'section' },
        { key: 'api_host', label: 'API Host', type: 'text', required: true, default: 'api.zeptomail.in', placeholder: 'api.zeptomail.in' },
        { key: 'api_token', label: 'API Token (Zoho-enczapikey)', type: 'password', required: true, placeholder: 'Zoho-enczapikey PHtE6r1Z...' },
        { key: 'mail_agent', label: 'Mail Agent ID', type: 'text', required: true, placeholder: '6085f22286ca4568' },
        { key: 'from_email', label: 'From Email', type: 'email', required: true, placeholder: 'noreply@yourdomain.com' },
        { key: 'from_name', label: 'From Name', type: 'text', required: true, default: 'Aniyor Marketplace' },
        { key: 'section_smtp', label: 'SMTP Configuration (Required for Emails)', type: 'section' },
        { key: 'smtp_host', label: 'SMTP Server', type: 'text', required: true, default: 'smtp.zeptomail.in', placeholder: 'smtp.zeptomail.in' },
        { key: 'smtp_port', label: 'SMTP Port', type: 'select', options: ['465', '587'], required: true, default: '587' },
        { key: 'smtp_user', label: 'SMTP Username', type: 'text', required: true, default: 'emailapikey', placeholder: 'emailapikey' },
        { key: 'smtp_password', label: 'SMTP Password (API Token)', type: 'password', required: true, placeholder: 'Your ZeptoMail SMTP password/token' },
        { key: 'smtp_security', label: 'Security Protocol', type: 'select', options: ['TLS', 'SSL'], required: true, default: 'TLS' }
      ]
    },
    aws_s3: {
      service: 'aws_s3',
      category: 'storage',
      integration_name: 'AWS S3 Storage',
      description: 'Cloud storage for product images, documents, and media files',
      fields: [
        { key: 'access_key_id', label: 'AWS Access Key ID', type: 'text', required: true },
        { key: 'secret_access_key', label: 'AWS Secret Access Key', type: 'password', required: true },
        { key: 'bucket_name', label: 'S3 Bucket Name', type: 'text', required: true },
        { key: 'region', label: 'AWS Region', type: 'text', required: true, default: 'ap-south-1' }
      ]
    },
    do_spaces: {
      service: 'do_spaces',
      category: 'storage',
      integration_name: 'DigitalOcean Spaces',
      description: 'Cloud storage via DigitalOcean Spaces - for product images, documents, and media files',
      fields: [
        { key: 'access_key_id', label: 'DO Access Key ID', type: 'text', required: true },
        { key: 'secret_access_key', label: 'DO Secret Access Key', type: 'password', required: true },
        { key: 'bucket_name', label: 'Space Name', type: 'text', required: true },
        { key: 'region', label: 'Region', type: 'text', required: true, default: 'sgp1' },
        { key: 'endpoint_url', label: 'Endpoint URL', type: 'text', required: true, placeholder: 'https://sgp1.digitaloceanspaces.com' },
        { key: 'cdn_endpoint', label: 'CDN Endpoint (Optional)', type: 'text', required: false, placeholder: 'https://your-space.sgp1.cdn.digitaloceanspaces.com' }
      ]
    },
    razorpay: {
      service: 'razorpay',
      category: 'financial',
      integration_name: 'Razorpay Payments',
      description: 'Payment gateway for accepting online payments - UPI, cards, netbanking, wallets',
      fields: [
        { key: 'key_id', label: 'Razorpay Key ID', type: 'text', required: true },
        { key: 'key_secret', label: 'Razorpay Key Secret', type: 'password', required: true },
        { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', required: false },
        { key: 'is_test_mode', label: 'Test Mode', type: 'checkbox', required: false, default: true }
      ]
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await apiCall('/integration-configs/');
      setIntegrations(response || []);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if user is super admin
  const isSuperAdmin = () => {
    return user?.role === 'super_admin' || 
           user?.is_super_admin === true || 
           user?.admin_type === 'super_admin';
  };

  // Fetch full credentials for editing (super admin only)
  const fetchFullCredentials = async (service) => {
    // Only attempt fetch if user is super_admin
    if (!isSuperAdmin()) {
      return {};
    }
    
    try {
      const response = await apiCall(`/integration-configs/${service}/full`);
      return response.credentials || {};
    } catch (error) {
      // Don't log 403 errors as they're expected for non-super-admins
      if (!error.message?.includes('403') && !error.message?.includes('Only super admins')) {
        console.error('Error fetching full credentials:', error);
      }
      return {};
    }
  };

  const handleConfigureIntegration = (integrationKey) => {
    const def = integrationDefinitions[integrationKey];
    setSelectedIntegration(def);
    setIsEditMode(false);
    setViewingConfig(null);
    
    // Initialize credentials with default values
    const initialCreds = {};
    def.fields.forEach(field => {
      if (field.default) {
        initialCreds[field.key] = field.default;
      }
    });
    setCredentials(initialCreds);
  };

  const handleViewConfig = async (integrationKey, config) => {
    setViewingConfig(config);
    setSelectedIntegration(null);
    setIsEditMode(false);
    
    // Fetch full credentials for viewing (super_admin only)
    if (isSuperAdmin()) {
      try {
        const fullCreds = await fetchFullCredentials(config.service);
        setViewingCredentials(fullCreds);
      } catch (error) {
        console.error('Could not fetch full credentials:', error);
        setViewingCredentials(null);
      }
    } else {
      setViewingCredentials(null);
    }
  };

  const handleEditConfig = async (integrationKey, config) => {
    const def = integrationDefinitions[integrationKey] || Object.values(integrationDefinitions).find(d => d.service === config.service);
    if (!def) return;
    
    setSelectedIntegration(def);
    setIsEditMode(true);
    setViewingConfig(null);
    
    // Fetch full credentials for editing
    const fullCreds = await fetchFullCredentials(config.service);
    setCredentials(fullCreds);
  };

  const handleSaveConfiguration = async () => {
    if (!selectedIntegration) return;

    // Validate required fields
    const missingFields = selectedIntegration.fields
      .filter(f => f.required && !credentials[f.key])
      .map(f => f.label);

    if (missingFields.length > 0) {
      toast({
        title: 'Missing Required Fields',
        description: `Please fill: ${missingFields.join(', ')}`,
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      // Check if configuration exists
      const existingConfig = integrations.find(i => i.service === selectedIntegration.service);

      if (existingConfig) {
        // Update existing
        await apiCall(`/integration-configs/${selectedIntegration.service}`, {
          method: 'PUT',
          body: JSON.stringify({ credentials })
        });
      } else {
        // Create new
        await apiCall('/integration-configs/', {
          method: 'POST',
          body: JSON.stringify({
            service: selectedIntegration.service,
            category: selectedIntegration.category,
            integration_name: selectedIntegration.integration_name,
            description: selectedIntegration.description,
            credentials
          })
        });
      }

      toast({
        title: 'Success',
        description: 'Integration configured successfully'
      });

      // Refresh list
      await fetchIntegrations();
      setSelectedIntegration(null);
      setCredentials({});
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save configuration',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (service) => {
    try {
      const result = await apiCall(`/integration-configs/test/${service}`, {
        method: 'POST'
      });

      toast({
        title: result.status === 'success' ? 'Success' : 'Test Pending',
        description: result.message
      });

      // Refresh to show updated test status
      await fetchIntegrations();
    } catch (error) {
      toast({
        title: 'Test Failed',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Delete/Clear integration configuration
  const handleDeleteConfig = async (service) => {
    if (!window.confirm(`Are you sure you want to delete the configuration for ${service}? This will reset it to "Not Configured" status.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await apiCall(`/integration-configs/${service}`, {
        method: 'DELETE'
      });
      
      toast({
        title: 'Success',
        description: `Configuration for ${service} deleted successfully`
      });
      
      // Refresh list and close any open modals
      await fetchIntegrations();
      setViewingConfig(null);
      setViewingCredentials(null);
      setSelectedIntegration(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete configuration',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (integration) => {
    const config = integrations.find(i => i.service === integration.service);
    
    if (!config) {
      return <Badge variant="outline" className="text-gray-500">Not Configured</Badge>;
    }

    // Check if credentials are actually configured (has any fields)
    const hasCredentials = config.credential_fields && config.credential_fields.length > 0;
    
    if (!hasCredentials) {
      return <Badge variant="outline" className="text-gray-500">Not Configured</Badge>;
    }

    if (config.test_status === 'success') {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> Connected</Badge>;
    }

    if (config.test_status === 'failed') {
      return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" /> Failed</Badge>;
    }

    // Configured but not yet tested
    return <Badge className="bg-blue-100 text-blue-800"><AlertCircle className="h-3 w-3 mr-1" /> Configured</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">API Configurations</h1>
              <p className="text-gray-600">
                Configure marketplace-level services by inputting master API credentials
              </p>
            </div>
            {(user?.admin_type === 'super_admin' || user?.permissions?.is_super_admin || user?.role === 'super_admin') && (
              <Button 
                variant={showEnvEditor ? "default" : "outline"} 
                onClick={() => setShowEnvEditor(!showEnvEditor)}
                className="gap-2"
                data-testid="env-editor-btn"
              >
                <FileCode className="h-4 w-4" />
                {showEnvEditor ? 'Hide .env Editor' : '.env Editor'}
              </Button>
            )}
          </div>
        </div>

        {/* Integration Grid */}
        {!selectedIntegration && !viewingConfig && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {Object.entries(integrationDefinitions).map(([key, def]) => {
              const config = integrations.find(i => i.service === def.service);
            
            return (
              <Card key={key} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <Settings className="h-6 w-6 text-blue-600" />
                    {getStatusBadge(def)}
                  </div>
                  <CardTitle className="mt-4">{def.integration_name}</CardTitle>
                  <CardDescription>{def.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="text-sm text-gray-500">
                      <strong>Category:</strong> {def.category.replace('_', ' ').toUpperCase()}
                    </div>
                    
                    {config && config.last_tested && (
                      <div className="text-xs text-gray-400">
                        Last tested: {new Date(config.last_tested).toLocaleString()}
                      </div>
                    )}

                    <div className="flex gap-2 flex-wrap">
                      {config ? (
                        <>
                          {/* View Details - All admins */}
                          <Button
                            onClick={() => handleViewConfig(key, config)}
                            variant="outline"
                            size="sm"
                          >
                            <Eye className="h-4 w-4 mr-1" /> View
                          </Button>
                          
                          {/* Edit - Super Admin only (check using helper function) */}
                          {isSuperAdmin() && (
                            <Button
                              onClick={() => handleEditConfig(key, config)}
                              variant="outline"
                              size="sm"
                            >
                              <Edit className="h-4 w-4 mr-1" /> Edit
                            </Button>
                          )}
                          
                          {/* Test Connection */}
                          <Button
                            onClick={() => handleTestConnection(def.service)}
                            variant="outline"
                            size="icon"
                            title="Test Connection"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          
                          {/* Delete - Super Admin only */}
                          {isSuperAdmin() && (
                            <Button
                              onClick={() => handleDeleteConfig(def.service)}
                              variant="outline"
                              size="icon"
                              title="Delete Configuration"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              disabled={deleting}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          onClick={() => handleConfigureIntegration(key)}
                          variant="default"
                          className="flex-1"
                        >
                          Configure
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Configuration Details (Read-only) */}
      {viewingConfig && (
        <div className="max-w-2xl">
          <Button 
            variant="ghost" 
            className="mb-4"
            onClick={() => {
              setViewingConfig(null);
              setViewingCredentials(null);
            }}
          >
            ← Back to integrations
          </Button>

          <Card>
            <CardHeader>
              <CardTitle>{viewingConfig.integration_name}</CardTitle>
              <CardDescription>{viewingConfig.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Status:</span>
                  <span className="ml-2 font-medium">{viewingConfig.is_active ? 'Active' : 'Inactive'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Category:</span>
                  <span className="ml-2 font-medium">{viewingConfig.category}</span>
                </div>
                <div>
                  <span className="text-gray-500">Last Tested:</span>
                  <span className="ml-2 font-medium">{viewingConfig.last_tested ? new Date(viewingConfig.last_tested).toLocaleString() : 'Never'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Test Status:</span>
                  <span className={`ml-2 font-medium ${viewingConfig.test_status === 'success' ? 'text-green-600' : viewingConfig.test_status === 'failed' ? 'text-red-600' : ''}`}>
                    {viewingConfig.test_status || 'Not tested'}
                  </span>
                </div>
              </div>

              <Separator />

              {/* Show full credentials for super admin */}
              {isSuperAdmin() && viewingCredentials ? (
                <div>
                  <h4 className="font-medium mb-3">Configured Credentials:</h4>
                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    {Object.entries(viewingCredentials).map(([key, value]) => {
                      const isPassword = key.toLowerCase().includes('secret') || 
                                         key.toLowerCase().includes('password') || 
                                         key.toLowerCase().includes('token') ||
                                         key.toLowerCase().includes('key');
                      return (
                        <div key={key} className="flex items-start justify-between gap-4">
                          <span className="text-sm text-gray-600 font-medium min-w-[140px]">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:
                          </span>
                          <div className="flex items-center gap-2 flex-1">
                            <code className="text-sm bg-white px-2 py-1 rounded border flex-1 break-all">
                              {showCredentials[key] || !isPassword 
                                ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value || '-')) 
                                : '••••••••'}
                            </code>
                            {isPassword && value && (
                              <button
                                type="button"
                                onClick={() => setShowCredentials({
                                  ...showCredentials,
                                  [key]: !showCredentials[key]
                                })}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                {showCredentials[key] ? (
                                  <EyeOff className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <Eye className="h-4 w-4 text-gray-500" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {Object.keys(viewingCredentials).length === 0 && (
                      <p className="text-gray-500 text-sm">No credentials configured</p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <h4 className="font-medium mb-2">Configured Fields:</h4>
                  <div className="space-y-2">
                    {viewingConfig.credential_fields?.map((field, idx) => (
                      <div key={field.id || field.name || `${field.title || "field"}-${idx}`} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span>{field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                      </div>
                    ))}
                    {(!viewingConfig.credential_fields || viewingConfig.credential_fields.length === 0) && (
                      <p className="text-gray-500 text-sm">No credentials configured</p>
                    )}
                  </div>
                </div>
              )}

              {/* Action buttons - show for super_admin */}
              {isSuperAdmin() && (
                <div className="pt-4 flex gap-3 flex-wrap">
                  <Button 
                    onClick={() => {
                      const def = Object.values(integrationDefinitions).find(d => d.service === viewingConfig.service);
                      if (def) {
                        handleEditConfig(viewingConfig.service, viewingConfig);
                      }
                    }}
                    variant="outline"
                  >
                    <Edit className="h-4 w-4 mr-1" /> Edit Configuration
                  </Button>
                  <Button 
                    onClick={() => handleDeleteConfig(viewingConfig.service)}
                    variant="destructive"
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete Configuration'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Configuration Form */}
      {selectedIntegration && (
        <div className="max-w-2xl">
          <Button 
            variant="ghost" 
            className="mb-4"
            onClick={() => {
              setSelectedIntegration(null);
              setCredentials({});
              setIsEditMode(false);
              setShowCredentials({});
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to All Integrations
          </Button>
          
          <Card>
            <CardHeader>
              <CardTitle>
                {isEditMode ? 'Edit' : 'Configure'} {selectedIntegration.integration_name}
              </CardTitle>
              <CardDescription>
                {selectedIntegration.description}
                {isEditMode && (
                  <span className="block mt-1 text-yellow-600">
                    ⚠️ Editing existing configuration - all fields will be updated
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedIntegration.fields.map(field => (
                <div key={field.key} className="space-y-2">
                  {field.type === 'section' ? (
                    // Section header - visual separator
                    <div className="pt-4 pb-2 border-t border-gray-200 mt-4">
                      <h4 className="text-sm font-semibold text-gray-700">{field.label}</h4>
                    </div>
                  ) : field.type === 'checkbox' ? (
                    <div className="flex items-center gap-2">
                      <input
                        id={field.key}
                        type="checkbox"
                        checked={credentials[field.key] === true || credentials[field.key] === 'true'}
                        onChange={(e) => setCredentials({...credentials, [field.key]: e.target.checked})}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Label htmlFor={field.key} className="cursor-pointer">
                        {field.label}
                      </Label>
                    </div>
                  ) : (
                    <>
                      <Label htmlFor={field.key}>
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      
                      {field.type === 'textarea' ? (
                        <Textarea
                          id={field.key}
                          value={credentials[field.key] || ''}
                          onChange={(e) => setCredentials({...credentials, [field.key]: e.target.value})}
                          placeholder={field.placeholder || `Enter ${field.label}`}
                          rows={3}
                          className="font-mono text-sm"
                        />
                      ) : field.type === 'select' ? (
                        <select
                          id={field.key}
                          value={credentials[field.key] || ''}
                          onChange={(e) => setCredentials({...credentials, [field.key]: e.target.value})}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="">Select {field.label}</option>
                          {field.options?.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="relative">
                          <Input
                            id={field.key}
                            type={showCredentials[field.key] ? 'text' : (field.type === 'number' ? 'text' : field.type)}
                            value={credentials[field.key] !== undefined ? String(credentials[field.key]) : ''}
                            onChange={(e) => setCredentials({...credentials, [field.key]: e.target.value})}
                            placeholder={field.placeholder || `Enter ${field.label}`}
                          />
                          {field.type === 'password' && (
                            <button
                              type="button"
                              onClick={() => setShowCredentials({
                                ...showCredentials,
                                [field.key]: !showCredentials[field.key]
                              })}
                              className="absolute right-3 top-1/2 -translate-y-1/2"
                            >
                              {showCredentials[field.key] ? (
                                <EyeOff className="h-4 w-4 text-gray-400" />
                              ) : (
                                <Eye className="h-4 w-4 text-gray-400" />
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    setSelectedIntegration(null);
                    setCredentials({});
                    setIsEditMode(false);
                    setShowCredentials({});
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveConfiguration}
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? 'Saving...' : 'Save Configuration'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── .ENV FILE EDITOR ─── */}
      {showEnvEditor && (
        <div className="mt-6">
          <Card className="border-2 border-amber-200">
            <CardHeader className="bg-amber-50/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileCode className="h-5 w-5 text-amber-600" />
                  <CardTitle className="text-lg">Environment File Editor</CardTitle>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowEnvEditor(false)}>Close</Button>
              </div>
              <CardDescription className="flex items-start gap-2 mt-1">
                <ShieldAlert className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-amber-700">Direct access to backend .env file. Changes require backend restart. MONGO_URL and DB_NAME are protected and cannot be edited.</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <Button variant="outline" size="sm" onClick={async () => {
                  try {
                    const data = await apiCall('/integration-configs/env-editor/read');
                    setEnvVars(data.env_vars || {});
                    setEnvEditing({...data.env_vars});
                    toast({ title: 'Loaded', description: `${data.total_keys} environment variables loaded` });
                  } catch (e) {
                    toast({ title: 'Error', description: 'Failed to read .env', variant: 'destructive' });
                  }
                }}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Load .env
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEnvShowValues(!envShowValues)}>
                  {envShowValues ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                  {envShowValues ? 'Hide Values' : 'Show Values'}
                </Button>
              </div>

              {Object.keys(envEditing).length > 0 ? (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  {Object.entries(envEditing).sort((a, b) => a[0].localeCompare(b[0])).map(([key, value]) => {
                    const isProtected = key === 'MONGO_URL' || key === 'DB_NAME';
                    const isChanged = value !== envVars[key];
                    const isSensitive = /secret|password|token|key|api_key/i.test(key);
                    return (
                      <div key={key} className={`flex items-center gap-2 p-2 rounded-lg ${isChanged ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'} ${isProtected ? 'opacity-60' : ''}`}>
                        <Label className="w-1/3 text-xs font-mono truncate flex-shrink-0" title={key}>
                          {key}
                          {isChanged && <span className="text-blue-600 ml-1">*</span>}
                        </Label>
                        <Input
                          type={envShowValues || !isSensitive ? 'text' : 'password'}
                          value={value}
                          onChange={(e) => setEnvEditing(prev => ({...prev, [key]: e.target.value}))}
                          disabled={isProtected}
                          className="flex-1 font-mono text-xs h-8"
                          data-testid={`env-${key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">Click "Load .env" to view and edit environment variables</p>
              )}

              {Object.keys(envEditing).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  {!envConfirmOpen ? (
                    <Button 
                      onClick={() => setEnvConfirmOpen(true)}
                      className="w-full bg-amber-600 hover:bg-amber-700"
                      data-testid="env-save-btn"
                    >
                      <Save className="h-4 w-4 mr-2" /> Save Changes to .env
                    </Button>
                  ) : (
                    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <ShieldAlert className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-red-800">Confirm .env File Write</p>
                          <p className="text-xs text-red-600 mt-1">
                            This will directly modify the backend .env file. Changes will require a backend restart to take effect. 
                            This action is logged in the audit trail.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="destructive" 
                          className="flex-1"
                          disabled={envSaving}
                          onClick={async () => {
                            setEnvSaving(true);
                            try {
                              const result = await apiCall('/integration-configs/env-editor/write', {
                                method: 'POST',
                                body: JSON.stringify({ env_content: envEditing, confirmation_code: 'CONFIRM_ENV_WRITE' })
                              });
                              toast({ title: 'Saved', description: result.message });
                              setEnvVars({...envEditing});
                              setEnvConfirmOpen(false);
                            } catch (e) {
                              toast({ title: 'Failed', description: e.message || 'Write failed', variant: 'destructive' });
                            } finally {
                              setEnvSaving(false);
                            }
                          }}
                          data-testid="env-confirm-write-btn"
                        >
                          {envSaving ? 'Writing...' : 'Yes, Write to .env File'}
                        </Button>
                        <Button variant="outline" onClick={() => setEnvConfirmOpen(false)} className="flex-1">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default IntegrationSettings;
